import React, { useState, useEffect, useCallback, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function FilingCopilot({ uuid, filingGuide: initialGuide, filingUrl, onBack }) {
  const [guide, setGuide] = useState(initialGuide || null);
  const [loading, setLoading] = useState(!initialGuide);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [completedFields, setCompletedFields] = useState(new Set());
  const [checkedPrereqs, setCheckedPrereqs] = useState(new Set());
  const [copied, setCopied] = useState(null);
  const [liveViewUrl, setLiveViewUrl] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [browserLoading, setBrowserLoading] = useState(false);
  const [browserError, setBrowserError] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileTab, setMobileTab] = useState('guide');
  const browserStarted = useRef(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Restore persisted progress
  useEffect(function restoreProgress() {
    try {
      var saved = localStorage.getItem('filing-copilot-' + uuid);
      if (saved) setCompletedFields(new Set(JSON.parse(saved)));
      var prereqs = localStorage.getItem('filing-prereqs-' + uuid);
      if (prereqs) setCheckedPrereqs(new Set(JSON.parse(prereqs)));
    } catch (e) { /* ignore */ }
  }, [uuid]);

  useEffect(function handleResize() {
    var h = function() { setIsMobile(window.innerWidth < 768); };
    window.addEventListener('resize', h);
    return function() { window.removeEventListener('resize', h); };
  }, []);

  // Persist progress
  useEffect(function persistFields() {
    localStorage.setItem('filing-copilot-' + uuid, JSON.stringify(Array.from(completedFields)));
  }, [completedFields, uuid]);

  useEffect(function persistPrereqs() {
    localStorage.setItem('filing-prereqs-' + uuid, JSON.stringify(Array.from(checkedPrereqs)));
  }, [checkedPrereqs, uuid]);

  // Generate guide if not provided
  useEffect(function fetchGuide() {
    if (initialGuide) return;
    setLoading(true);
    fetch(API_BASE + '/api/legal-services/' + uuid + '/filing-guide', { credentials: 'include' })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.success && d.filingGuide) {
          setGuide(d.filingGuide);
          setLoading(false);
        } else {
          // Generate
          return fetch(API_BASE + '/api/legal-services/' + uuid + '/filing-guide', { method: 'POST', credentials: 'include' })
            .then(function(r2) { return r2.json(); })
            .then(function(d2) {
              if (d2.success) setGuide(d2.filingGuide);
              else setError(d2.error || 'Failed to generate guide');
              setLoading(false);
            });
        }
      })
      .catch(function(e) { setError(e.message); setLoading(false); });
  }, [uuid, initialGuide]);

  // Start Browserbase session when guide loads
  useEffect(function startBrowser() {
    if (!guide || browserStarted.current) return;
    browserStarted.current = true;
    setBrowserLoading(true);
    var url = guide.filingPortalUrl || filingUrl || '';
    fetch(API_BASE + '/api/legal-services/' + uuid + '/browser-session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: url }),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.success && d.liveViewUrl) {
          setLiveViewUrl(d.liveViewUrl);
          setSessionId(d.sessionId);
        } else {
          setBrowserError(d.error || 'Failed to start browser session');
        }
        setBrowserLoading(false);
      })
      .catch(function(e) { setBrowserError(e.message); setBrowserLoading(false); });
  }, [guide, uuid, filingUrl]);

  // Cleanup session on unmount
  useEffect(function cleanup() {
    return function() {
      if (sessionId) {
        fetch(API_BASE + '/api/legal-services/browser-session/' + sessionId + '/end', {
          method: 'POST', credentials: 'include',
        }).catch(function() {});
      }
    };
  }, [sessionId]);

  var toggleField = useCallback(function(key) {
    setCompletedFields(function(prev) {
      var next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  var togglePrereq = useCallback(function(idx) {
    setCheckedPrereqs(function(prev) {
      var next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  }, []);

  var copyValue = useCallback(function(text, key) {
    navigator.clipboard.writeText(text).then(function() {
      setCopied(key);
      setTimeout(function() { setCopied(null); }, 2000);
    });
  }, []);

  function askCopilot(e) {
    if (e) e.preventDefault();
    var question = chatInput.trim();
    if (!question || chatLoading) return;
    setChatInput('');
    setChatMessages(function(prev) { return prev.concat({ role: 'user', text: question }); });
    setChatLoading(true);
    fetch(API_BASE + '/api/legal-services/' + uuid + '/copilot-ask', {
      method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ question: question, currentPage: currentPage, sessionId: sessionId }),
    })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        setChatMessages(function(prev) { return prev.concat({ role: 'assistant', text: d.answer || d.error || 'Sorry, I could not answer that.' }); });
        setChatLoading(false);
        setTimeout(function() { if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' }); }, 100);
      })
      .catch(function() {
        setChatMessages(function(prev) { return prev.concat({ role: 'assistant', text: 'Failed to get answer. Please try again.' }); });
        setChatLoading(false);
      });
  }

  // Loading state
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, flexDirection: 'column', gap: 16 }}>
        <div style={{ width: 36, height: 36, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ fontSize: 14, color: '#64748b' }}>Generating your step-by-step filing guide...</p>
        <style>{('@keyframes spin { to { transform: rotate(360deg); } }')}</style>
      </div>
    );
  }

  // Error state
  if (error || !guide || guide.error) {
    return (
      <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>
        <p style={{ fontSize: 16, fontWeight: 500, color: '#ef4444', marginBottom: 8 }}>Failed to generate filing guide</p>
        <p style={{ fontSize: 13, marginBottom: 16 }}>{error || (guide && guide.error) || 'Unknown error'}</p>
        <button onClick={onBack} style={{ padding: '8px 20px', borderRadius: 8, border: '1px solid #d1d5db', background: 'white', cursor: 'pointer', fontSize: 13 }}>Back to Results</button>
      </div>
    );
  }

  var pages = guide.pages || [];
  var page = pages[currentPage] || null;
  var totalFields = pages.reduce(function(sum, p) { return sum + (p.fields ? p.fields.length : 0); }, 0);
  var completedCount = completedFields.size;
  var progressPct = totalFields > 0 ? Math.round((completedCount / totalFields) * 100) : 0;
  var portalUrl = guide.filingPortalUrl || filingUrl || '#';

  function renderGuide() {
    return (
      <div style={{ width: isMobile ? '100%' : '22%', minWidth: isMobile ? undefined : 260, maxWidth: 340, height: '100%', display: 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : '1px solid #e2e8f0', background: '#fafbfc', flexShrink: 0 }}>
        <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0', background: 'white' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6366f1', fontWeight: 500 }}>
              ← Back to Results
            </button>
            <span style={{ fontSize: 11, color: '#94a3b8' }}>{guide.estimatedTime}</span>
          </div>
          <h3 style={{ fontSize: 15, fontWeight: 600, margin: 0, color: '#1e293b' }}>Filing Copilot</h3>
          <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 8px' }}>{guide.portalName || guide.stateName} — {guide.stateName}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ width: progressPct + '%', height: '100%', background: progressPct === 100 ? '#22c55e' : '#6366f1', borderRadius: 3, transition: 'width 0.3s' }} />
            </div>
            <span style={{ fontSize: 11, color: '#64748b', whiteSpace: 'nowrap' }}>{completedCount}/{totalFields}</span>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 11, color: '#92400e', lineHeight: 1.4 }}>
            AI-generated guide. Field names may differ slightly from the actual portal.
          </div>

          {guide.prerequisites && guide.prerequisites.length > 0 && currentPage === 0 && (
            <div style={{ marginBottom: 16 }}>
              <h4 style={{ fontSize: 12, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' }}>Before you start</h4>
              {guide.prerequisites.map(function(p, i) {
                return (
                  <label key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '6px 0', cursor: 'pointer', fontSize: 13, color: '#334155' }}>
                    <input type="checkbox" checked={checkedPrereqs.has(i)} onChange={function() { togglePrereq(i); }} style={{ marginTop: 2, accentColor: '#6366f1' }} />
                    <span><strong>{p.item}</strong>{p.detail ? ' — ' + p.detail : ''}</span>
                  </label>
                );
              })}
            </div>
          )}

          {page && (
            <div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', margin: 0 }}>
                    Page {page.pageNumber}: {page.pageTitle}
                  </h4>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{currentPage + 1} of {pages.length}</span>
                </div>
                <p style={{ fontSize: 13, color: '#64748b', margin: '4px 0 0' }}>{page.instructions}</p>
              </div>

              {page.fields && page.fields.map(function(field, fi) {
                var fieldKey = currentPage + '-' + fi;
                var isDone = completedFields.has(fieldKey);
                return (
                  <div key={fi} style={{
                    background: isDone ? '#f0fdf4' : 'white',
                    border: '1px solid ' + (field.important ? '#fbbf24' : isDone ? '#86efac' : '#e2e8f0'),
                    borderRadius: 10, padding: '12px 14px', marginBottom: 10, transition: 'all 0.2s',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: '#1e293b' }}>{field.fieldLabel}</span>
                      {field.important && <span style={{ fontSize: 10, fontWeight: 600, color: '#d97706', background: '#fef3c7', padding: '2px 6px', borderRadius: 4 }}>Critical</span>}
                    </div>
                    {field.valueToEnter && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '6px 10px', marginBottom: 6 }}>
                        <span style={{ fontSize: 11, color: '#6366f1', fontWeight: 600, flexShrink: 0 }}>Enter:</span>
                        <span style={{ fontSize: 13, color: '#0c4a6e', fontWeight: 500, flex: 1, wordBreak: 'break-word' }}>{field.valueToEnter}</span>
                        <button onClick={function() { copyValue(field.valueToEnter, fieldKey); }} style={{
                          background: copied === fieldKey ? '#22c55e' : '#e2e8f0',
                          color: copied === fieldKey ? 'white' : '#475569',
                          border: 'none', borderRadius: 4, padding: '3px 8px', fontSize: 11, cursor: 'pointer', flexShrink: 0, fontWeight: 500,
                        }}>
                          {copied === fieldKey ? 'Copied!' : 'Copy'}
                        </button>
                      </div>
                    )}
                    <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px', lineHeight: 1.4 }}>{field.helpText}</p>
                    <button onClick={function() { toggleField(fieldKey); }} style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      background: isDone ? '#dcfce7' : '#f1f5f9',
                      border: '1px solid ' + (isDone ? '#86efac' : '#cbd5e1'),
                      borderRadius: 6, padding: '4px 12px', fontSize: 12, cursor: 'pointer',
                      color: isDone ? '#166534' : '#475569', fontWeight: 500,
                    }}>
                      {isDone ? '✓ Done' : 'Mark as done'}
                    </button>
                  </div>
                );
              })}

              {page.tip && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#1e40af', marginTop: 4 }}>
                  <strong>Tip:</strong> {page.tip}
                </div>
              )}
            </div>
          )}

          {currentPage === pages.length - 1 && progressPct === 100 && guide.postSubmission && (
            <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 16, marginTop: 12 }}>
              <h4 style={{ fontSize: 14, fontWeight: 600, color: '#166534', margin: '0 0 8px' }}>After Submission</h4>
              <p style={{ fontSize: 13, color: '#15803d', margin: '0 0 8px' }}>{guide.postSubmission.confirmationNote}</p>
              {guide.postSubmission.nextSteps && guide.postSubmission.nextSteps.map(function(s, i) {
                return <p key={i} style={{ fontSize: 12, color: '#166534', margin: '4px 0', paddingLeft: 12 }}>{'• ' + s}</p>;
              })}
            </div>
          )}
        </div>

        {/* Chat messages */}
        {chatMessages.length > 0 && (
          <div style={{ borderTop: '1px solid #e2e8f0', maxHeight: 200, overflowY: 'auto', padding: '8px 16px' }}>
            {chatMessages.map(function(msg, i) {
              return (
                <div key={i} style={{ marginBottom: 8, display: 'flex', justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div style={{
                    maxWidth: '90%', padding: '6px 10px', borderRadius: 8, fontSize: 12, lineHeight: 1.4,
                    background: msg.role === 'user' ? '#6366f1' : '#f1f5f9',
                    color: msg.role === 'user' ? 'white' : '#1e293b',
                  }}>
                    {msg.text}
                  </div>
                </div>
              );
            })}
            {chatLoading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                <div style={{ padding: '6px 10px', borderRadius: 8, fontSize: 12, background: '#f1f5f9', color: '#94a3b8' }}>Thinking...</div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
        )}

        {/* Chat input + page nav */}
        <div style={{ borderTop: '1px solid #e2e8f0', background: 'white' }}>
          <form onSubmit={askCopilot} style={{ display: 'flex', gap: 6, padding: '8px 12px', borderBottom: '1px solid #f1f5f9' }}>
            <input
              value={chatInput}
              onChange={function(e) { setChatInput(e.target.value); }}
              placeholder="What should I do here?"
              style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: '1px solid #e2e8f0', fontSize: 12, outline: 'none' }}
            />
            <button type="submit" disabled={chatLoading || !chatInput.trim()} style={{
              padding: '6px 12px', borderRadius: 6, border: 'none', background: '#6366f1', color: 'white',
              fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: chatLoading || !chatInput.trim() ? 0.5 : 1, flexShrink: 0,
            }}>
              Ask
            </button>
          </form>
          <div style={{ padding: '6px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button onClick={function() { setCurrentPage(function(p) { return Math.max(0, p - 1); }); }} disabled={currentPage === 0}
              style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: 'white', fontSize: 11, cursor: currentPage === 0 ? 'default' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, color: '#374151' }}>
              ←
            </button>
            <span style={{ fontSize: 10, color: '#94a3b8' }}>{currentPage + 1}/{pages.length}</span>
            <button onClick={function() { setCurrentPage(function(p) { return Math.min(pages.length - 1, p + 1); }); }} disabled={currentPage >= pages.length - 1}
              style={{ padding: '4px 12px', borderRadius: 6, border: 'none', background: currentPage >= pages.length - 1 ? '#e2e8f0' : '#6366f1', color: currentPage >= pages.length - 1 ? '#94a3b8' : 'white', fontSize: 11, cursor: currentPage >= pages.length - 1 ? 'default' : 'pointer', fontWeight: 600 }}>
              →
            </button>
          </div>
        </div>
      </div>
    );
  }

  function renderBrowser() {
    if (browserLoading) {
      return (
        <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: '#1e1e1e' }}>
          <div style={{ width: 36, height: 36, border: '3px solid #333', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
          <p style={{ fontSize: 14, color: '#94a3b8' }}>Opening {guide.portalName || 'filing portal'}...</p>
          <p style={{ fontSize: 11, color: '#64748b' }}>Starting a secure browser session</p>
          <style>{('@keyframes spin { to { transform: rotate(360deg); } }')}</style>
        </div>
      );
    }
    if (browserError) {
      return (
        <div style={{ flex: 1, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: 24, background: '#1e1e1e' }}>
          <p style={{ fontSize: 14, color: '#f87171' }}>Could not start browser session</p>
          <p style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>{browserError}</p>
          <a href={portalUrl} target="_blank" rel="noopener noreferrer"
            style={{ padding: '8px 20px', borderRadius: 8, background: '#6366f1', color: 'white', fontSize: 13, fontWeight: 500, textDecoration: 'none', marginTop: 8 }}>
            Open portal in new tab instead
          </a>
        </div>
      );
    }
    if (liveViewUrl) {
      return (
        <div style={{ flex: 1, height: '100%', background: '#1e1e1e' }}>
          <iframe
            src={liveViewUrl}
            title="Filing Portal - Live Browser"
            sandbox="allow-same-origin allow-scripts"
            allow="clipboard-read; clipboard-write"
            style={{ width: '100%', height: '100%', border: 'none', background: 'white' }}
          />
        </div>
      );
    }
    return <div style={{ flex: 1, background: '#1e1e1e' }} />;
  }

  if (isMobile) {
    return (
      <div style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', background: 'white' }}>
          <button onClick={function() { setMobileTab('guide'); }} style={{ flex: 1, padding: '10px', fontSize: 13, fontWeight: 600, border: 'none', borderBottom: mobileTab === 'guide' ? '2px solid #6366f1' : '2px solid transparent', color: mobileTab === 'guide' ? '#6366f1' : '#64748b', background: 'none', cursor: 'pointer' }}>
            Instructions
          </button>
          <button onClick={function() { setMobileTab('portal'); }} style={{ flex: 1, padding: '10px', fontSize: 13, fontWeight: 600, border: 'none', borderBottom: mobileTab === 'portal' ? '2px solid #6366f1' : '2px solid transparent', color: mobileTab === 'portal' ? '#6366f1' : '#64748b', background: 'none', cursor: 'pointer' }}>
            Filing Portal
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'hidden' }}>
          {mobileTab === 'guide' ? renderGuide() : renderBrowser()}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 64px)', overflow: 'hidden' }}>
      {renderGuide()}
      {renderBrowser()}
    </div>
  );
}
